import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Grid, TextField, Button, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import { SUPER_ADMIN } from "common/constant";
import {
  getOptionLabel,
  getOptionValue,
  otherUtilityBillingOptions,
} from "common/enums";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { useLazyGetOtherMonthlyBillingReport } from "apis/OtherBillingReport/otherBillingReport";
import { useLazyDownloadExcelQuery } from "slices/api";

const OtherMonthlyBillingReport = ({ t }) => {
  const { utilityRoles } = useSelector((state) => state.Login.user);
  const { roles } = useSelector((state) => state.Login);

  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const [
    getOtherBillingReports,
    {
      data: { data: billingReportsData } = {},
      isLoading: isBillingReportLoading,
      isFetching: isBillingReportFetching,
    },
  ] = useLazyGetOtherMonthlyBillingReport();

  const sm = useMediaQuery("(max-width: 599px)");

  const [billCycleId, setBillCycleId] = useState("");
  const [{ utilityId, subscriptionType }, setUtility] = useState({
    utilityId: "",
    subscriptionType: "",
  });
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();
  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType: currentSubscriptionType }) =>
                currentSubscriptionType === subscriptionType
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions, subscriptionType]
  );

  const otherUtilityFilteredOptions = useMemo(() => {
    const utilityRoleIds = utilityRoles.map(({ utilityId }) => utilityId);
    return roles.includes(SUPER_ADMIN)
      ? otherUtilityBillingOptions
      : otherUtilityBillingOptions.filter(({ value }) =>
          utilityRoleIds.includes(value)
        );
  }, [otherUtilityBillingOptions, utilityRoles]);

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Invoice Number",
        field: "invoiceNumber",
      },
      {
        title: "Consumer Name",
        field: "consumerName",
      },
      {
        title: "Premise Area",
        field: "premiseArea",
      },
      {
        title: "Taxable Amount",
        field: "taxableAmount",
      },
      {
        title: "GST Amount",
        field: "gstAmount",
      },
      {
        title: "Total Amount With GST",
        field: "totalBillAmountWithGST",
      },
      {
        title: "Net Payable",
        field: "netPayable",
      },

      {
        title: "Invoice Date",
        renderCell: ({ invoiceDate }) =>
          moment(invoiceDate).isValid()
            ? convertUTCDateToLocalDate(invoiceDate).format("DD-MM-YYYY")
            : "N/A",
      },
    ],
    []
  );

  const downloadBtnHandler = async () => {
    try {
      if (utilityId) {
        await downloadExcel({
          endpoint: "report/other-utility-monthly-billing-report-excel",
          sheetName: `${getOptionLabel(
            "otherUtilityApplicationOptions",
            utilityId
          )} Monthly Billing ${
            updatedBillingCycleOptions.find(
              ({ value }) => value === billCycleId
            ).label
          }`,
          query: {
            utility: utilityId,
            billingCycleId: billCycleId,
          },
        });
      }
    } catch (error) {}
  };

  const getOtherUtilityReportsData = async (query) => {
    try {
      await getOtherBillingReports(query);
    } catch (error) {}
  };

  const handleClear = () => {
    const billCycleId =
      updatedBillingCycleOptions.find(
        ({ status }) => status !== getOptionValue("billingStatus", "Closed")
      )?.value ??
      updatedBillingCycleOptions.at(-1)?.value ??
      "";
    const utilityId = otherUtilityFilteredOptions[0].value;
    const subscriptionType = otherUtilityFilteredOptions[0].subscriptionType;
    setUtility({
      utilityId: utilityId,
      subscriptionType: subscriptionType,
    });
    setBillCycleId(billCycleId);
    getOtherUtilityReportsData({
      billingCycleId: billCycleId,
      utility: utilityId,
    });
    handleResetPage();
  };

  useEffect(() => {
    if (otherUtilityFilteredOptions.length) {
      setUtility({
        utilityId: otherUtilityFilteredOptions[0].value,
        subscriptionType: otherUtilityFilteredOptions[0].subscriptionType,
      });
    }
  }, [otherUtilityFilteredOptions]);

  useEffect(() => {
    if (updatedBillingCycleOptions.length && subscriptionType && utilityId) {
      const billingCycleId =
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "";
      setBillCycleId(billingCycleId);
      getOtherUtilityReportsData({
        billingCycleId,
        utility: utilityId,
      });
    }
  }, [updatedBillingCycleOptions]);

  return (
    <div className="pb-2">
      <Backdrop
        open={
          isBillingReportFetching ||
          isBillingReportLoading ||
          isExcelFileDownloading
        }
      />
      <BreadCrumb
        title={t("Monthly Billing Report")}
        pageTitle={t("Billing")}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getOtherUtilityReportsData({
            billingCycleId: billCycleId,
            utility: utilityId,
          });
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} className="mb-1 mt-2">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Utility")}
              onChange={(_, { value, subscriptionType }) => {
                setUtility({
                  utilityId: value,
                  subscriptionType: subscriptionType,
                });
              }}
              options={otherUtilityFilteredOptions}
              value={
                otherUtilityFilteredOptions.find(
                  ({ value }) => value === utilityId
                ) ?? ""
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              onChange={(_, { value }) => setBillCycleId(value)}
              value={
                updatedBillingCycleOptions.find(
                  (status) => status.value === billCycleId
                ) ?? ""
              }
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return <TextField {...params} label={t("Select bill cycle")} />;
              }}
              options={updatedBillingCycleOptions}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              disabled={!updatedBillingCycleOptions.length}
              className=" me-2"
              type="submit"
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              disabled={!updatedBillingCycleOptions.length}
              onClick={handleClear}
              className=" me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadBtnHandler}
              disabled={!billCycleId}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>

      <BaseTable
        data={billingReportsData || []}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        pageSize={10}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

OtherMonthlyBillingReport.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OtherMonthlyBillingReport);
